import React from 'react';
// MUI
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
// Navigation
import { useNavigate } from 'react-router-dom';
// Icons
import DownloadIcon from '@mui/icons-material/Download';
// Interfaces
import { PartnerResource } from '../../../interfaces/partnerResources.interface';
// Hooks
import { useAppSelector } from '../../../app/hooks';
import { useTheme } from '@mui/material/styles';
// Components
import ResourceCard from '../ResourceCard';
// Helpers
import { gradientColors } from '../../../services/partnerResourcesHelpers';
import { USER_TYPES } from '../../../services/user';

const StandardLibrary = ({ 
  filteredSections,
  expandedSection,
  handleSetPartnerResource,
  isPreview = false,
}: {
  expandedSection: null | string;
  filteredSections: PartnerResource[];
  handleSetPartnerResource: (resource: PartnerResource | null) => void;
  isPreview?: boolean;
}) => {
  const { user } = useAppSelector(state => state.user);
  const theme = useTheme();
  const navigate = useNavigate();

  const handleCardClick = (document: PartnerResource) => {
    if (isPreview) {
      handleSetPartnerResource(document);
      return;
    }

    navigate(`/partner-resources/${expandedSection}/${document.id}`);
  };
    
  return (
    <>
    {filteredSections.map((item, index) => (
      <Grid item xs={12} sm={6} md={3} key={item.id}>
          <Card
              elevation={0}
              sx={{
                height: 325,
                border: '1px solid rgba(0,0,0,.06)',
                boxSizing: 'border-box',
                padding: 1,
                borderRadius: 2.5,
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
                transformOrigin: 'center',
                '&:hover': {
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                  borderColor: '#eeeeee',
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={() => handleCardClick(item)}
          >
            <Box
              sx={{
                position: 'relative',
                p: 1.5,
                borderRadius: 2,
                background: () => {
                  const g = gradientColors[index % gradientColors.length];
                  return `linear-gradient(135deg, ${g.start} 0%, ${g.end} 100%)`;
                },
              }}
              >
                <ResourceCard item={item} />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 2,
                    background:
                      'linear-gradient(180deg, rgba(0,0,0,.10) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,.12) 100%)',
                    pointerEvents: 'none',
                  }}
                />
              </Box>
              <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    height: 'calc(100% - 140px)',
                    pt: 3,
                    px: 1,
                  }}
              >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: '#2F2D4F',
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        marginLeft: '5px',
                        fontWeight: 400,
                        color: '#666666',
                      }}
                    >
                      by {item?.author?.displayName || 'Trusted Partner Network'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        marginTop: 1,
                        color: '#333333',
                        lineHeight: 1.6,
                        letterSpacing: '0.3px',
                      }}
                    >
                      {item.subDescription}
                    </Typography>
                  </Box>
              </CardContent>
          </Card>
          { user?.type === USER_TYPES.TPN_ADMIN && !isPreview && (
          <Box sx={{ mt: 1, px: 1 }}>
              <Typography variant="caption" sx={{ color: theme.palette.customThemeColors?.darkGrey }}>
                  <DownloadIcon sx={{ fontSize: 'small', verticalAlign: 'middle', mr: 0.5 }} />
                  {item.metadata.length} Download{item.metadata.length === 1 ? '' : 's'}
              </Typography>
          </Box>
          )}
      </Grid>
    ))}
    </>
  );
};

export default StandardLibrary;